import { STEP } from "#src/constants/step.ts";
import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import {
  type DataRequirements,
  REQS,
} from "#src/constants/data-requirements.ts";

import { useStepBuildStore } from "#src/stores/step-build.ts";
import { useStepHealthStore } from "#src/stores/step-health.ts";
import {
  type SmokerFormArgs,
  useSmokerForm,
} from "#src/components/steps/smoker/smoker-form.composable.ts";
import { computed } from "vue";
import {
  type BaseStepStoreState,
  type ApiFlowStep,
} from "#src/types/StepStore.ts";
import { type InsuredType } from "#src/types/Insured.ts";
import { type StoreGeneric, type Pinia } from "pinia";

interface SmokerState extends BaseStepStoreState {}
interface SmokerStep extends ApiFlowStep, ToSetupState<SmokerState> {
  setStepRequirements(srq: DataRequirements): void;
}

function initialState(): SmokerState {
  return {
    inactive: false,
  };
}

function initializeSmokerStep(pinia: Pinia): SmokerStep {
  const state = generateInitialState<SmokerState>(initialState());

  function $reset() {
    resetState<SmokerState>(state, initialState());
  }

  const step = computed(() => STEP.SMOKER);
  const next = computed(() => {
    const build = useStepBuildStore(pinia);
    if (build.inactive) return build.next;
    return build.step;
  });
  const previous = computed(() => {
    const health = useStepHealthStore(pinia);
    if (health.inactive) return health.previous;
    return health.step;
  });

  function setStepRequirements(srq: DataRequirements) {
    state.inactive.value = !srq[REQS.SMOKER];
  }
  function generateForm(module: InsuredType, args: SmokerFormArgs) {
    return useSmokerForm(pinia, module, args);
  }

  return {
    $reset,
    step,
    ...state,
    next,
    previous,
    setStepRequirements,
    generateForm,
  };
}

export const useStepSmokerStore = (pinia: Pinia, hot?: StoreGeneric) => {
  return defineStore<string, SmokerStep>("step-smoker", () => {
    return initializeSmokerStep(pinia);
  })(pinia, hot);
};
